import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api-client'
import type { AxiosError } from 'axios'

export interface Project {
  id: number
  name: string
  location?: string
  startDate?: string
  endDate?: string
  totalValue?: number
  maintenanceDuration?: number
  status: 'draft' | 'active' | 'completed'
}

/**
 * Hook to fetch single project by ID
 */
export function useProject(projectId: number) {
  return useQuery<Project, AxiosError>({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const response = await apiClient.getProject(projectId)
      return response.data
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Hook to fetch all projects
 */
export function useProjects() {
  return useQuery<Project[], AxiosError>({
    queryKey: ['projects'],
    queryFn: async () => {
      const response = await apiClient.getProjects()
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to create a new project
 */
export function useCreateProject() {
  const queryClient = useQueryClient()

  return useMutation<Project, AxiosError, Omit<Project, 'id'>>({
    mutationFn: async (projectData) => {
      const response = await apiClient.createProject(projectData)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: (error) => {
      console.error('Failed to create project:', error.response?.data)
    }
  })
}

/**
 * Hook to update a project
 */
export function useUpdateProject() {
  const queryClient = useQueryClient()

  return useMutation<Project, AxiosError, { projectId: number; data: Partial<Project> }>({
    mutationFn: async ({ projectId, data }) => {
      const response = await apiClient.updateProject(projectId, data)
      return response.data
    },
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
    },
    onError: (error) => {
      console.error('Failed to update project:', error.response?.data)
    }
  })
}

/**
 * Hook to delete a project
 */
export function useDeleteProject() {
  const queryClient = useQueryClient()

  return useMutation<{ success: boolean }, AxiosError, number>({
    mutationFn: async (projectId) => {
      const response = await apiClient.deleteProject(projectId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
    onError: (error) => {
      console.error('Failed to delete project:', error.response?.data)
    }
  })
}
